import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import config from 'config';

interface DatadogUser {
  id: string | number;
  email?: string;
  role?: string | null;
}

type DatadogContextValue = string | number | boolean | undefined;

export type DatadogContext = Record<string, DatadogContextValue>;

export const DATADOG_EVENT = {
  networkSessionView: 'network_session_view',
  ruleStatusChanged: 'rule_status_changed',
  ruleEdited: 'rule_edited',
  ruleDuplicated: 'rule_duplicated',
  emergencyToggled: 'emergency_toggled',
  userSessionStarted: 'user_session_started',
  urlSessionView: 'url_session_view',
} as const;

const DEFAULT_ENV = window.location.hostname.includes('-stage.asqq.io')
  ? 'stage'
  : window.location.hostname.includes('-dev.asqq.io')
    ? 'dev'
    : window.location.hostname.includes('localhost')
      ? 'local'
      : 'production';

let isDatadogInitialized = false;

const initDatadogRum = () => {
  if (
    isDatadogInitialized ||
    import.meta.env.MODE === 'test' ||
    !config?.DATADOG_APPLICATION_ID ||
    !config?.DATADOG_CLIENT_TOKEN
  ) {
    return;
  }

  console.info('[Datadog] Initializing datadog Rum', {
    DATADOG_APPLICATION_ID: !!config.DATADOG_APPLICATION_ID,
    DATADOG_CLIENT_TOKEN: !!config.DATADOG_CLIENT_TOKEN,
  });

  datadogRum.init({
    applicationId: config.DATADOG_APPLICATION_ID,
    clientToken: config.DATADOG_CLIENT_TOKEN,
    site: config.DATADOG_SITE || 'datadoghq.com',
    service: config.DATADOG_SERVICE || 'autorules-admin',
    env: DEFAULT_ENV,
    version: '1.0.0',
    // capture 100% of sessions so RUM computes metrics accurately across full app traffic
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    // router: true enables automatic route change tracking + view name normalization
    // for dynamic segments (e.g. /networks/:network/rules)
    plugins: [reactPlugin({ router: true })],
  });

  isDatadogInitialized = true;
};

const setDatadogUser = ({ id, email, role }: DatadogUser) => {
  if (!isDatadogInitialized) {
    return;
  }

  datadogRum.setUser({
    id: String(id),
    email,
    role: role ?? undefined,
  });
};

const trackDatadogAction = (name: string, context?: DatadogContext) => {
  if (!isDatadogInitialized) {
    return;
  }

  datadogRum.addAction(name, context);
};

const trackDatadogError = (error: Error | string, context?: DatadogContext) => {
  if (!isDatadogInitialized) {
    return;
  }

  datadogRum.addError(error, context);
};

const datadogActions = {
  initDatadogRum,
  setDatadogUser,
  trackDatadogAction,
  trackDatadogError,
};

export default datadogActions;
export * from './networkTracking';
export * from './ruleTracking';
export * from './emergencyTracking';
export * from './sessionTracking';
